/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import {
    ProgressIndicator,
    Box,
    Spacer,
    ContentBlock,
    Heading,
    Text,
    useAssetImages,
    Stack,
    Alert,
} from 'sarsaparilla';

import {
    OrderDetailsLayout,
    OrderDetailsSection,
    OrderDetailsSummary,
} from 'shared-ui/components/OrderDetailsLayout';

import { LOTTERY_CHOICES_PROP_PATH } from 'ui-permit/shared-components/constants/propPaths';
import { issuanceStatuses, setLotteryButtonLabels } from 'ui-permit/src/utils/utilsNew';
import { LOTTERY_APPLIED } from 'ui-permit/src/constants/shared-constants';
import ErrorNotification from 'ui-permit/shared-components/common/ErrorNotification';

import {
    HuntingPermitProps,
    LotteryProps,
    HuntingLotteryApplicationProps,
} from '../../props';
import { databindings } from '../../constants/registration';
import LotteryReservationDetail from './LotteryReservationDetail';
import LotteryRegistrationForms, {
    LotteryAfterFormComponents,
} from './LotteryRegistrationForms';
import { progressSteps } from '../../constants';

const propTypes = {
    permit: HuntingPermitProps.isRequired,
    lottery: LotteryProps.isRequired,
    reservation: HuntingLotteryApplicationProps.isRequired,
    seasonYear: PropTypes.number,
    permitTypeID: PropTypes.string,
    validations: PropTypes.shape({
        summary: PropTypes.array,
        displayValidationError: PropTypes.bool,
    }).isRequired,
    errors: PropTypes.arrayOf(PropTypes.string).isRequired,
    isAddingToCart: PropTypes.bool,
    updatePendingReservation: PropTypes.func.isRequired,
    formValidationChange: PropTypes.func.isRequired,
    addToCart: PropTypes.func.isRequired,
    removeFromCart: PropTypes.func.isRequired,
    continueShopping: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    reservationId: PropTypes.string,
};

const LotteryRegistration = React.memo((props) => {
    const {
        permit,
        lottery,
        reservation,
        permitTypeID,
        validations,
        errors,
        isAddingToCart,
        addToCart,
        removeFromCart,
        continueShopping,
        isLoading,
        reservationId,
    } = props;

    const imageObject = useAssetImages({ assetId: permit.id });

    const lotteryName = lottery?.lottery?.name || '';
    const permitType = get(permit, 'permit_types_list', []).find(
        (pt) => pt.id === permitTypeID
    );
    const displayName = permitType?.name ?? '';

    const renderErrors = () => {
        const displayValidationErrors =
            validations.summary.length && validations.displayValidationError;
        if (!displayValidationErrors) return null;

        const validationErrorBullets = sortBy(validations?.summary || [], 'path').map(
            (summary) => ({ ...summary.error, key: summary.path })
        );

        return (
            <div aria-live="polite">
                <Alert
                    className="mb-2"
                    type="error"
                    heading={`Form has ${validations.summary.length} validation errors. Please
                    correct them before you can proceed to payment.`}
                    hasAriaLive
                >
                    <ul key="validation-errors-detail">{validationErrorBullets}</ul>
                </Alert>
            </div>
        );
    };

    // TODO right now changes to any applied lotteries should not generate any fee
    const amountDue =
        issuanceStatuses.get(reservation.status) === LOTTERY_APPLIED ? 0 : 1;

    const buttonLabels = setLotteryButtonLabels(reservation, amountDue);
    const summary = (
        <OrderDetailsSummary
            isLoading={isLoading}
            heading={displayName}
            imageUrl={imageObject.previewImage?.url ?? ''}
            isWaitingForServerResponse={isAddingToCart}
            parentName={`${permit.name || ''} - Lottery`}
            isProceedToCartDisabled={isAddingToCart}
            onProceedToCartClick={addToCart}
            proceedToCartButtonText={buttonLabels.proceed}
            secondaryButtonText={buttonLabels.continue}
            onSecondaryButtonClick={continueShopping}
            showBuyNow
            reservationId={reservationId}
        >
            <LotteryReservationDetail
                permit={permit}
                issuance={reservation}
                selectedDates={get(
                    reservation,
                    databindings[LOTTERY_CHOICES_PROP_PATH],
                    []
                )}
            />
        </OrderDetailsSummary>
    );

    return (
        <>
            <Box background="gray-2" paddingY="sm">
                <ProgressIndicator steps={progressSteps} activeStep={1} />
            </Box>
            <Spacer size="xl" />
            <ContentBlock>
                <OrderDetailsLayout summaryElement={summary}>
                    <ErrorNotification className="permit-error-wrap" errors={errors} />
                    {renderErrors()}
                    <Heading headingLevel={1} appearance="h3">
                        Registration
                    </Heading>
                    <Heading headingLevel={2} appearance="h5Caps" className="mt-1">
                        <Text color="gray" size="xl" fontWeight="semibold">
                            {lotteryName}
                        </Text>
                    </Heading>
                    <OrderDetailsSection
                        heading={permit.name}
                        onRemoveClick={removeFromCart}
                        isLoading={isLoading}
                    >
                        <Stack space="lg" className="my-2 mx-xs-1 mx-sm-3">
                            <LotteryRegistrationForms {...props} />
                        </Stack>
                    </OrderDetailsSection>
                    <LotteryAfterFormComponents {...props} />
                </OrderDetailsLayout>
            </ContentBlock>
        </>
    );
});

LotteryRegistration.propTypes = propTypes;

export default LotteryRegistration;
